import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import getLocation from '../getLocation';
import ApiClient from '../../config/api';
import sendLiveLocation from './index';
import {
  startNativeLiveLocation,
  stopNativeLiveLocation,
} from './nativeBridge';

const LOCATION_INTERVAL_MS = 30000;
const SEND_THROTTLE_MS = 5000;
const LOCATION_TIMEOUT_MS = 20000;
const MIN_DISTANCE_METERS = Number(
  process.env.REACT_APP_LIVE_LOCATION_MIN_DISTANCE_METERS || 20
);

const parseJson = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const isAuthenticated = () => Boolean(parseJson(localStorage.getItem('info'))?.token);

const parseMessageData = (data) => {
  if (typeof data !== 'string') return data;

  return parseJson(data) || data;
};

const normalizePosition = (position) => ({
  coords: {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    altitude: position.coords.altitude || 0,
    accuracy: position.coords.accuracy || 0,
    altitudeAccuracy: position.coords.altitudeAccuracy || 0,
    heading: position.coords.heading || 0,
    speed: position.coords.speed || 0,
  },
  timestamp: position.timestamp || Date.now(),
});

const getCoordinates = (location) => {
  const lat = Number(
    location?.coords?.latitude ?? location?.latitude ?? location?.lat
  );
  const lng = Number(
    location?.coords?.longitude ?? location?.longitude ?? location?.lng
  );

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

  return { lat, lng };
};

const toRadians = (degree) => (degree * Math.PI) / 180;

const calculateDistanceMeters = (first, second) => {
  const earthRadiusMeters = 6371000;
  const deltaLat = toRadians(second.lat - first.lat);
  const deltaLng = toRadians(second.lng - first.lng);
  const firstLat = toRadians(first.lat);
  const secondLat = toRadians(second.lat);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(firstLat) *
      Math.cos(secondLat) *
      Math.sin(deltaLng / 2) *
      Math.sin(deltaLng / 2);

  return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const hasMovedEnough = (lastCoordinates, nextCoordinates) => {
  if (!lastCoordinates) return true;

  return (
    calculateDistanceMeters(lastCoordinates, nextCoordinates) >=
    MIN_DISTANCE_METERS
  );
};

const LiveLocationTracker = () => {
  const api = ApiClient();
  const dutyActive = useSelector((st) => st?.global?.dutyActive);
  const lastLocationRef = useRef(null);
  const lastSentCoordinatesRef = useRef(null);
  const lastSentAtRef = useRef(0);
  const fallbackTimerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated() || !dutyActive) {
      stopNativeLiveLocation();
      return undefined;
    }

    const sendLocation = (location, force = false) => {
      if (!location) return;

      lastLocationRef.current = location;
      const nextCoordinates = getCoordinates(location);
      if (!nextCoordinates) return;

      if (!hasMovedEnough(lastSentCoordinatesRef.current, nextCoordinates)) {
        return;
      }

      const now = Date.now();
      if (!force && now - lastSentAtRef.current < SEND_THROTTLE_MS) {
        return;
      }

      lastSentAtRef.current = now;
      lastSentCoordinatesRef.current = nextCoordinates;
      sendLiveLocation(api, location);
    };

    const requestBrowserLocation = () => {
      if (!('geolocation' in navigator)) return;

      navigator.geolocation.getCurrentPosition(
        (position) => sendLocation(normalizePosition(position)),
        (error) => {
          console.error('Live location geolocation error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: LOCATION_TIMEOUT_MS,
          maximumAge: 0,
        }
      );
    };

    const requestCurrentLocation = () => {
      const cycleStartedAt = Date.now();

      startNativeLiveLocation();
      getLocation();
      if (document.visibilityState !== 'hidden') {
        requestBrowserLocation();
      }

      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = setTimeout(() => {
        if (
          lastLocationRef.current &&
          lastSentAtRef.current < cycleStartedAt
        ) {
          sendLocation(lastLocationRef.current, true);
        }
      }, 5000);
    };
    const handleMessage = (event) => {
      const message = parseMessageData(event?.data);

      if (message?.type === 'location') {
        sendLocation(message.data);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestCurrentLocation();
      }
    };

    window.addEventListener('message', handleMessage);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    let watchId;
    if ('geolocation' in navigator) {
      watchId = navigator.geolocation.watchPosition(
        (position) => sendLocation(normalizePosition(position)),
        (error) => {
          console.error('Live location watch error:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: LOCATION_TIMEOUT_MS,
          maximumAge: 0,
        }
      );
    }

    requestCurrentLocation();
    const intervalId = setInterval(requestCurrentLocation, LOCATION_INTERVAL_MS);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
      clearTimeout(fallbackTimerRef.current);

      if (watchId !== undefined && 'geolocation' in navigator) {
        navigator.geolocation.clearWatch(watchId);
      }
      stopNativeLiveLocation();
    };
  }, [dutyActive]);

  return null;
};

export default LiveLocationTracker;
